import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ChevronRight, Sparkles, Smile, Frown, Meh } from 'lucide-react';

/**
 * ConversationIntelligence - Topics with sentiment analysis
 * Shows what people are asking and how they feel about each topic
 */
const ConversationIntelligence = ({ topics = [], totalConversations = 0 }) => {
    const [expandedTopic, setExpandedTopic] = useState(null);
    const maxCount = topics.length > 0 ? topics[0].count : 1;

    const getTrend = (index) => {
        const trends = ['up', 'up', 'stable', 'down', 'stable'];
        return trends[index % trends.length];
    };

    const getBarColor = (index) => {
        const colors = ['#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b'];
        return colors[index % colors.length];
    };

    const getSentimentIcon = (sentiment) => {
        // Always show an icon - default to neutral if no sentiment data
        if (!sentiment) return <Meh size={12} className="text-slate-300" title="No sentiment data" />;
        if (sentiment.label === 'positive') return <Smile size={12} className="text-emerald-500" title="Positive sentiment" />;
        if (sentiment.label === 'negative') return <Frown size={12} className="text-rose-500" title="Negative sentiment" />;
        return <Meh size={12} className="text-slate-400" title="Neutral sentiment" />;
    };

    const getSentimentLabel = (sentiment) => {
        if (!sentiment) return <span className="text-[9px] text-slate-400">analyzing...</span>;
        const { positive, negative, neutral } = sentiment;
        const total = positive + negative + neutral;
        if (total === 0) return <span className="text-[9px] text-slate-400">neutral</span>;

        if (sentiment.label === 'negative' && negative > 0) {
            const percent = Math.round((negative / total) * 100);
            return <span className="text-[9px] text-rose-500">{percent}% frustrated</span>;
        }
        if (sentiment.label === 'positive' && positive > 0) {
            const percent = Math.round((positive / total) * 100);
            return <span className="text-[9px] text-emerald-500">{percent}% happy</span>;
        }
        return <span className="text-[9px] text-slate-400">neutral</span>;
    };

    if (!topics || topics.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
                <div className="flex items-center gap-1.5 mb-4">
                    <Sparkles size={14} className="text-amber-500" />
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">What People Are Asking</h3>
                </div>
                <div className="text-center py-10">
                    <p className="text-sm text-slate-500">No topic patterns detected yet</p>
                    <p className="text-xs text-slate-400 mt-1">Topics appear as conversations accumulate</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
            {/* Header */}
            <div className="p-5 border-b border-slate-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <Sparkles size={14} className="text-amber-500" />
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">What People Are Asking</h3>
                    </div>
                    <span className="text-[10px] text-slate-400">
                        from {totalConversations.toLocaleString()} conversations
                    </span>
                </div>
            </div>

            {/* Topic List */}
            <div className="divide-y divide-slate-50">
                {topics.map((topic, index) => {
                    const percentage = ((topic.count / maxCount) * 100).toFixed(0);
                    const trend = getTrend(index);
                    const isExpanded = expandedTopic === index;
                    const barColor = getBarColor(index);

                    return (
                        <div key={index}>
                            <button
                                onClick={() => setExpandedTopic(isExpanded ? null : index)}
                                className="w-full p-4 hover:bg-slate-50/50 transition-colors text-left"
                            >
                                <div className="flex items-center gap-3">
                                    {/* Rank with color */}
                                    <span
                                        className="w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center text-white"
                                        style={{ backgroundColor: barColor }}
                                    >
                                        {index + 1}
                                    </span>

                                    {/* Topic + Bar */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-slate-700 truncate pr-2">
                                                    {topic.phrase}
                                                </span>
                                                {/* Sentiment indicator - always visible */}
                                                <div className="flex items-center gap-1">
                                                    {getSentimentIcon(topic.sentiment)}
                                                    {getSentimentLabel(topic.sentiment)}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {trend === 'up' && (
                                                    <span className="text-[10px] text-emerald-600 flex items-center font-medium">
                                                        <TrendingUp size={10} className="mr-0.5" /> Rising
                                                    </span>
                                                )}
                                                {trend === 'down' && (
                                                    <span className="text-[10px] text-rose-500 flex items-center font-medium">
                                                        <TrendingDown size={10} className="mr-0.5" /> Falling
                                                    </span>
                                                )}
                                                <span className="text-sm font-semibold text-slate-600 tabular-nums min-w-[36px] text-right">
                                                    {topic.count}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{ width: `${percentage}%`, backgroundColor: barColor }}
                                            />
                                        </div>
                                    </div>

                                    <ChevronRight
                                        size={14}
                                        className={`text-slate-300 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    />
                                </div>
                            </button>

                            {/* Expanded Examples */}
                            {isExpanded && topic.examples && topic.examples.length > 0 && (
                                <div className="px-4 pb-4 -mt-1">
                                    <div className="ml-8 pl-3 border-l-2" style={{ borderColor: barColor + '40' }}>
                                        <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Example Questions</p>
                                        <div className="space-y-1.5">
                                            {topic.examples.slice(0, 3).map((example, i) => (
                                                <div key={i} className="text-xs text-slate-500 bg-slate-50 rounded px-2 py-1.5">
                                                    "{example.length > 80 ? example.slice(0, 80) + '...' : example}"
                                                </div>
                                            ))}
                                        </div>
                                        {/* Sentiment breakdown if negative */}
                                        {topic.sentiment && topic.sentiment.negative > 0 && (
                                            <div className="mt-2 p-2 bg-rose-50 rounded text-[10px] text-rose-600">
                                                ⚠️ {topic.sentiment.negative} users expressed frustration about this topic
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-50/50 border-t border-slate-100">
                <p className="text-xs text-slate-400 text-center">
                    💡 Add FAQ answers for top topics to reduce support load
                </p>
            </div>
        </div>
    );
};

export default ConversationIntelligence;
